import { create } from 'zustand';
import axios from 'axios';
import axiosInstance from '../api/axiosInstance';
import useAuthStore from './useAuthStore';

interface QuestionOption {
  id: number;
  option: string;
}

interface Question {
  id: number;
  question: string;
  selected: string | number | string[] | number[];
  active: boolean;
  questions_options: QuestionOption[];
  step: number;
  allowMultiple: boolean;
}

interface Genre {
  id: number;
  name: string;
  title: string;
}

interface OnboardingState {
  registerLoading: boolean;
  uploadProgress: number;
  questions: Question[];
  currentStep: number;
  error: string | null;
  inputs: { [key: string]: string };
  uploadedFiles: File[];
  genres: Genre[];
  selectedGenres: number[];
  searchQuery: string;
  optionSearchTerms: { [questionId: number]: string };
  genresToShow: number;
  setSearchQuery: (query: string) => void;
  setOptionSearchTerm: (questionId: number, term: string) => void;
  showMoreGenres: () => void;
  showLessGenres: () => void;
  fetchGenres: () => Promise<void>;
  fetchQuestions: () => Promise<void>;
  toggleGenre: (id: number) => void;
  selectOption: (questionId: number, optionId: number) => void;
  handleInputChange: (inputName: string, value: string) => void;
  handleFileUpload: (file: File[]) => void;
  nextStep: () => void;
  prevStep: () => void;
  simpleRegister: () => Promise<void>;
  submitAnswers: () => Promise<any>;
  setUploadProgress: (progress: number) => void;
  errors: { [key: string]: string };
  validatefullName: () => boolean;
  validate: () => boolean;
  validate2: () => boolean;
  validate3: () => boolean;
  validate4: () => boolean;
  setErrors: (errors: { [key: string]: string }) => void;
}

// ✅ Helper: Browser se lat/lng lo — 5 sec timeout, error pe quietly resolve
const getGeolocation = (): Promise<{ lat: string; lang: string } | null> => {
  return new Promise((resolve) => {
    if (!navigator.geolocation) {
      resolve(null);
      return;
    }
    navigator.geolocation.getCurrentPosition(
      (position) => {
        resolve({
          lat: String(position.coords.latitude),
          lang: String(position.coords.longitude),
        });
      },
      () => resolve(null),
      { timeout: 5000 }
    );
  });
};

export const useOnboardingStore = create<OnboardingState>((set, get) => ({
  uploadProgress: 0,
  registerLoading: false,
  questions: [],
  currentStep: 1,
  error: null,
  inputs: {},
  uploadedFiles: [],
  genres: [],
  searchQuery: '',
  optionSearchTerms: {},
  selectedGenres: [],
  genresToShow: 5,
  setUploadProgress: (progress: any) => set({ uploadProgress: progress }),
  errors: {},
  setErrors: (errors) => set({ errors }),

  setOptionSearchTerm: (questionId, term) => {
    set((state) => ({
      optionSearchTerms: { ...state.optionSearchTerms, [questionId]: term },
    }));
  },

  validatefullName: () => {
    const { inputs } = get();
    const newErrors: { [key: string]: string } = {};
    if (!inputs["first_name"]) {
      newErrors["first_name"] = "Please enter your full name";
    } else if (inputs["first_name"].length > 20) {
      newErrors["first_name"] = "Full name must be 20 characters or less";
    }
    set({ errors: newErrors });
    return Object.values(newErrors).every(val => val === "");
  },

  validate: () => {
    const { inputs, questions } = get();
    const newErrors: { [key: string]: string } = {};

    if (!inputs["email"] || !/^[a-z0-9._%+-]+@[a-z0-9.-]+\.[a-z]{2,}$/.test(inputs["email"])) {
      newErrors["email"] = "Invalid email format";
    } else { newErrors["email"] = ""; }

    if (!inputs["password"]) {
      newErrors["password"] = "Password is required";
    } else if (!/^(?=.*[A-Z])(?=.*[^A-Za-z0-9])(?=.{8,})/.test(inputs["password"])) {
      newErrors["password"] = "Password must be at least 8 characters, including one uppercase letter and one special character.";
    } else { newErrors["password"] = ""; }

    if (inputs["password"] !== inputs["confirm-password"]) {
      newErrors["confirm-password"] = "Passwords do not match";
    } else { newErrors["confirm-password"] = ""; }

    if (!inputs["city"]) { newErrors["city"] = "City is required"; } else { newErrors["city"] = ""; }
    if (!inputs["state"]) { newErrors["state"] = "State is required"; } else { newErrors["state"] = ""; }
    if (!inputs["zipcode"]) { newErrors["zipcode"] = "Zipcode is required"; } else { newErrors["zipcode"] = ""; }

    const firstQuestion = questions?.[0];
    if (!firstQuestion?.selected) { newErrors["question"] = "Answer is required"; } else { newErrors["question"] = ""; }

    if (!inputs["dob-day"] || !inputs["dob-month"] || !inputs["dob-year"]) {
      newErrors["dob"] = "Date of Birth is required";
    } else { newErrors["dob"] = ""; }

    if (!inputs["tc_pp_agree"]) {
      newErrors["tc_pp_agree"] = "Terms-condition check is required";
    } else { newErrors["tc_pp_agree"] = ""; }

    set({ errors: newErrors });
    return Object.values(newErrors).every(val => val === "");
  },

  validate2: () => {
    const { inputs, questions, currentStep } = get();
    const newErrors: { [key: string]: string } = {};
    const stepQuestions = (questions || []).filter((q) => q.step === currentStep);

    stepQuestions.forEach((question) => {
      const isEmpty = question.allowMultiple
        ? !Array.isArray(question.selected) || question.selected.length === 0
        : !question.selected;
      if (isEmpty) newErrors[`question_${question.id}`] = "Answer is required";
    });

    if (!inputs["looking_min_age"]) newErrors["looking_min_age"] = "Minimum age is required";
    if (!inputs["looking_max_age"]) newErrors["looking_max_age"] = "Maximum age is required";

    set({ errors: newErrors });
    return Object.values(newErrors).every(val => val === "");
  },

  validate3: () => {
    const { inputs, questions, currentStep } = get();
    const newErrors: { [key: string]: string } = {};
    const stepQuestions = (questions || []).filter((q) => q.step === currentStep);

    stepQuestions.forEach((question) => {
      const isEmpty = question.allowMultiple
        ? !Array.isArray(question.selected) || question.selected.length === 0
        : !question.selected;
      if (isEmpty) newErrors[`question_${question.id}`] = "Answer is required";
    });

    if (!inputs["occupation"]) newErrors["occupation"] = "This field is required";
    if (!inputs["height"]) newErrors["height"] = "This field is required";

    set({ errors: newErrors });
    return Object.values(newErrors).every(val => val === "");
  },

  validate4: () => {
    const { inputs, questions, currentStep } = get();
    const newErrors: { [key: string]: string } = {};
    const stepQuestions = (questions || []).filter((q) => q.step === currentStep);

    stepQuestions.forEach((question) => {
      const isEmpty = question.allowMultiple
        ? !Array.isArray(question.selected) || question.selected.length === 0
        : !question.selected;
      if (isEmpty) newErrors[`question_${question.id}`] = "Answer is required";
    });

    if (!inputs["know_about_you"]) newErrors["know_about_you"] = "This field is required";

    set({ errors: newErrors });
    return Object.values(newErrors).every(val => val === "");
  },

  fetchQuestions: async () => {
    try {
      const response = await axiosInstance.get('/getRegisterQuestions');
      set((state) => {
        const previousSelections = state.questions.reduce((acc, q) => {
          acc[q.id] = q.allowMultiple
            ? (Array.isArray(q.selected) ? q.selected.filter((id): id is number => typeof id === "number") : [])
            : (Array.isArray(q.selected) ? "" : q.selected);
          return acc;
        }, {} as Record<number, string | number | number[]>);

        const questions = response.data.rows.map((val: any) => ({
          ...val,
          selected: previousSelections[val.id] || '',
          active: false,
          questions_options: val.questions_options.sort((a: any, b: any) => a.option.localeCompare(b.option)),
          step:
            val.number === 1 ? 1
              : [2, 3, 4, 5].includes(val.number) ? 3
                : [6, 7, 8, 9].includes(val.number) ? 4
                  : [10, 11, 12, 13, 14, 15, 16, 17, 18].includes(val.number) ? 5
                    : 0,
        }));

        questions.sort((a: any, b: any) => a.number - b.number);
        return { questions };
      });
    } catch (error) {
      console.error('Error fetching questions:', error);
    }
  },

  fetchGenres: async () => {
    try {
      const response = await axiosInstance.get('/getGenres');
      set({ genres: response.data.rows });
    } catch (error) {
      console.error('Error fetching genres:', error);
    }
  },

  toggleGenre: (id) => {
    set((state) => {
      const isSelected = state.selectedGenres.includes(id);
      return {
        selectedGenres: isSelected
          ? state.selectedGenres.filter((gId) => gId !== id)
          : [...state.selectedGenres, id],
      };
    });
  },

  setSearchQuery: (query) => set({ searchQuery: query }),
  showMoreGenres: () => set((state) => ({ genresToShow: state.genresToShow + 10 })),
  showLessGenres: () => set(() => ({ genresToShow: 5 })),

  selectOption: (questionId, optionId) =>
    set((state) => {
      const updatedQuestions = state.questions.map((q) => {
        if (q.id === questionId) {
          if (q.allowMultiple) {
            const selectedOptions = Array.isArray(q.selected)
              ? q.selected.filter((id): id is number => typeof id === "number")
              : [];
            const isSelected = selectedOptions.includes(optionId);
            return {
              ...q,
              selected: isSelected
                ? selectedOptions.filter((id) => id !== optionId)
                : [...selectedOptions, optionId],
            };
          } else {
            return { ...q, selected: optionId };
          }
        }
        return q;
      });

      const updatedErrors = { ...state.errors };
      const changedQuestion = updatedQuestions.find((q) => q.id === questionId);
      const hasValidAnswer =
        changedQuestion &&
        ((Array.isArray(changedQuestion.selected) && changedQuestion.selected.length > 0) ||
          (!Array.isArray(changedQuestion.selected) && changedQuestion.selected));

      if (hasValidAnswer) {
        updatedErrors[`question`] = "";
        updatedErrors[`question_${questionId}`] = "";
      }

      return { questions: updatedQuestions, errors: updatedErrors };
    }),

  handleInputChange: (field, value) => {
    set((state) => {
      const updatedInputs = { ...state.inputs, [field]: value };
      const updatedErrors = { ...state.errors };

      switch (field) {
        case "first_name":
          updatedErrors["first_name"] = !value ? "Full name is required"
            : value.length > 20 ? "Full name must be 20 characters or less" : "";
          break;
        case "email":
          updatedErrors["email"] =
            !value || !/^[a-z0-9._%+-]+@[a-z0-9.-]+\.[a-z]{2,}$/.test(value)
              ? "Invalid email format" : "";
          break;
        case "password":
          updatedErrors["password"] = !value ? "Password is required"
            : !/^(?=.*[A-Z])(?=.*[^A-Za-z0-9])(?=.{8,})/.test(value)
              ? "Password must be at least 8 characters, including one uppercase letter and one special character."
              : "";
          if (state.inputs["confirm-password"] && state.inputs["confirm-password"] !== value) {
            updatedErrors["confirm-password"] = "Passwords do not match";
          } else {
            updatedErrors["confirm-password"] = "";
          }
          break;
        case "confirm-password":
          updatedErrors["confirm-password"] =
            value !== updatedInputs["password"] ? "Passwords do not match" : "";
          break;
        case "city":
        case "state":
        case "zipcode":
          updatedErrors[field] = value ? "" : `${field} is required`;
          break;
        case "tc_pp_agree":
          updatedErrors["tc_pp_agree"] = value ? "" : "Terms-condition check is required";
          break;
        case "dob-day":
        case "dob-month":
        case "dob-year":
          const day = field === "dob-day" ? value : updatedInputs["dob-day"];
          const month = field === "dob-month" ? value : updatedInputs["dob-month"];
          const year = field === "dob-year" ? value : updatedInputs["dob-year"];
          updatedErrors["dob"] = day || month || year ? "" : "Date of Birth is required";
          break;
        case "know_about_you":
          updatedErrors["know_about_you"] =
            !value || value.length > 300 ? "Bio must be 300 characters or less" : "";
          break;
        default:
          updatedErrors[field] = value ? "" : updatedErrors[field];
          break;
      }

      return { inputs: updatedInputs, errors: updatedErrors };
    });
  },

  handleFileUpload: (files: File[]) => set(() => ({ uploadedFiles: files.slice(0, 5) })),
  nextStep: () => set((state) => ({ currentStep: state.currentStep + 1 })),
  prevStep: () => set((state) => ({ currentStep: state.currentStep - 1 })),

  simpleRegister: async () => {
    set({ registerLoading: true });

    // ✅ STEP 1: Store mein lat/lang check karo
    let currentState = useOnboardingStore.getState();
    let lat = currentState.inputs['lat'] || '';
    let lang = currentState.inputs['lang'] || '';

    // ✅ STEP 2: Agar lat/lang missing hai to browser geolocation se lo
    if (!lat || !lang) {
      console.log('[Register] lat/lang missing — fetching geolocation...');
      const geo = await getGeolocation();
      if (geo) {
        lat = geo.lat;
        lang = geo.lang;
        // Store update karo
        useOnboardingStore.setState((s) => ({
          inputs: { ...s.inputs, lat, lang },
        }));
        console.log('[Register] Geolocation success:', lat, lang);
      } else {
        console.log('[Register] Geolocation failed — registering without lat/lang');
      }
    }

    // ✅ STEP 3: Fresh state lo — geolocation update ke baad
    currentState = useOnboardingStore.getState();

    const answers = currentState.questions.map((q) => ({
      question_id: q.id,
      answer: q.selected,
    }));

    const dob = `${currentState.inputs['dob-year']}-${currentState.inputs['dob-month']}-${currentState.inputs['dob-day']}`;

    lat = currentState.inputs['lat'];
    lang = currentState.inputs['lang'];

    // 🚨 FINAL FAIL-SAFE: If location is missing, try IP-based lookup before registering
    if (!lat || !lang || Number(lat) === 0) {
      console.log('[Register] Location missing, attempting automatic recovery...');
      try {
        const backup = await axios.get('https://ipapi.co/json/');
        if (backup.data && backup.data.latitude) {
          lat = backup.data.latitude.toString();
          lang = backup.data.longitude.toString();
          console.log('[Register] Automatic recovery success:', lat, lang);
        }
      } catch (e) {
        console.error('[Register] Automatic recovery failed.');
      }
    }

    const inputFields = {
      email: currentState.inputs['email'] || '',
      password: currentState.inputs['password'] || '',
      confirm_password: currentState.inputs['confirm-password'] || '',
      city: currentState.inputs['city'] || '',
      state: currentState.inputs['state'] || '',
      zipcode: currentState.inputs['zipcode'] || '',
      dob: dob || '',
      lat: Number(lat) || 0,
      lang: Number(lang) || 0,
      tc_pp_agree: currentState.inputs['tc_pp_agree'] || '',
      first_name: currentState.inputs['first_name'] || '',
    };

    console.log('[Register] Sending lat/lang:', inputFields.lat, inputFields.lang);

    const formData = new FormData();
    formData.append('answers', JSON.stringify(answers));
    formData.append('inputFields', JSON.stringify(inputFields));

    currentState.uploadedFiles.forEach((file: any, index: any) => {
      formData.append(`images[${index}]`, file);
    });

    try {
      useAuthStore.getState().setEmailExistMessage("");
      const response = await axiosInstance.post('/registerUser', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });

      if (response.status !== 200) {
        set({ error: response.data.error || "Email already exists" });
        throw new Error('Failed to submit answers');
      }
      set((state) => ({ currentStep: state.currentStep + 1 }));
      set({ registerLoading: false });
    } catch (error: any) {
      console.error('Error submitting answers:', error);
      set({ error: error.response?.data?.error || "Registration failed" });
      set({ registerLoading: false });
    }
  },

  submitAnswers: async (): Promise<{ email: string; password: string } | void> => {
    const state = useOnboardingStore.getState();
    const setUploadProgress = state.setUploadProgress;
    const answers = state.questions.map((q: any) => ({
      question_id: q.id,
      answer: q.selected,
    }));

    let height = '';
    if (state.inputs['height']) {
      const feet = Math.floor(parseInt(state.inputs['height']) / 12);
      const inches = parseInt(state.inputs['height']) % 12;
      height = feet + '\' ' + inches + '"';
    }

    const convertHeightToCm = (height: any) => {
      height = height.replace(/'/g, "'").replace(/"/g, '"');
      const match = height.match(/(\d+)' (\d+)"/);
      if (!match) return "Invalid height";
      const feet = parseInt(match[1], 10);
      const inches = parseInt(match[2], 10);
      return ((feet * 30.48) + (inches * 2.54)).toFixed(0);
    };

    const inputFields = {
      email: state.inputs['email'] || '',
      password: state.inputs['password'] || '',
      confirm_password: state.inputs['confirm-password'] || '',
      city: state.inputs['city'] || '',
      state: state.inputs['state'] || '',
      zipcode: state.inputs['zipcode'] || '',
      lat: Number(state.inputs['lat']) || 0,
      lang: Number(state.inputs['lang']) || 0,
      genres: state.selectedGenres || '',
      height: height || '',
      height_cm: height ? convertHeightToCm(height) : '',
      looking_min_age: state.inputs['looking_min_age'] || '',
      looking_max_age: state.inputs['looking_max_age'] || '',
      occupation: state.inputs['occupation'] || '',
      number: state.inputs['number'] || '',
      tc_pp_agree: state.inputs['tc_pp_agree'] || '',
      first_name: state.inputs['first_name'] || '',
      profile_vibe: state.inputs['profile_vibe'] || '',
      know_about_you: state.inputs['know_about_you'] || '',
    };

    let simulatedProgress = 0;
    const interval = setInterval(() => {
      if (simulatedProgress < 90) {
        simulatedProgress += 2;
        setUploadProgress(simulatedProgress);
      }
    }, 100);

    const formData = new FormData();
    formData.append('answers', JSON.stringify(answers));
    formData.append('inputFields', JSON.stringify(inputFields));
    state.uploadedFiles.forEach((file, index) => {
      formData.append(`images[${index}]`, file);
    });

    try {
      const response = await axiosInstance.post('/registerOnboardingUser', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        onUploadProgress: (progressEvent) => {
          if (progressEvent.total) {
            let actualProgress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
            if (actualProgress >= 95) { actualProgress = 95; clearInterval(interval); }
            setUploadProgress(simulatedProgress);
          }
        }
      });

      clearInterval(interval);

      if (response.status !== 200) {
        throw new Error('Failed to submit answers');
      } else {
        let finalProgress = 95;
        while (finalProgress < 100) {
          finalProgress += 1;
          setUploadProgress(finalProgress);
          await new Promise((resolve) => setTimeout(resolve, 150));
        }
        setUploadProgress(0);
        set((state) => ({ currentStep: state.currentStep + 1 }));
        console.log('Answers submitted successfully');
        return { email: inputFields.email, password: inputFields.password };
      }
    } catch (error) {
      console.error('Error submitting answers:', error);
      setUploadProgress(0);
      clearInterval(interval);
    }
  },
}));